// Hunt maps. Every area gets its own top-down map, generated from the area id so
// the server (which needs walls for movement) and the client (which paints it)
// build exactly the same thing without sending it over the wire.
//
// Terrain comes from three smooth noise fields sampled in tile units:
//   elevation  -> low ground is water (or lava, or a frozen crevasse)
//   ridge      -> a thin band around the middle value is a rock ridge / cliff
//   forest     -> high values grow trees
// The server only looks at tile centres; the client samples the same fields per
// pixel so coastlines and ridges come out as curves rather than squares.
import { RAMPS } from '../art/palette.ts';
import { GROUND } from './grounds.ts';

export const MAP_W = 48;
export const MAP_H = 32;

export const T_GROUND = 0;
export const T_WATER = 1;
export const T_CLIFF = 2;
export const T_TREE = 3;
export const T_ROCK = 4;

type Ramp4 = readonly [string, string, string, string];

export interface Biome {
  id: string;
  /** Ground shades, dark to light. The darkest is only used for shadows and wet edges. */
  ground: Ramp4;
  /** A second ground material (dirt, paving, moss) laid in large patches. */
  patch: Ramp4;
  /** Deep, mid, light, foam. */
  liquid: Ramp4;
  liquidKind: 'water' | 'lava' | 'ice' | 'styx';
  cliff: Ramp4;
  water: number;
  ridge: number;
  forest: number;
  rocks: number;
  patchAmount: number;
  trees: string[];
  blockers: string[];
  decor: string[];
  decorDensity: number;
}

const R = RAMPS;
const B = (b: Partial<Biome> & Pick<Biome, 'id' | 'ground'>): Biome => ({
  patch: b.ground, liquid: [R.tide[0], R.tide[1], R.tide[2], R.tide[3]], liquidKind: 'water', cliff: R.stone,
  water: 0.12, ridge: 0.02, forest: 0.12, rocks: 0.012, patchAmount: 0.25,
  trees: [], blockers: [], decor: [], decorDensity: 0.05, ...b,
});

export const BIOMES: Record<string, Biome> = {
  snow: B({ id: 'snow', ground: [R.snow[0], R.snow[1], R.snow[2], R.snow[3]], patch: [R.frost[1], R.frost[2], R.snow[1], R.snow[2]], patchAmount: 0.2,
    trees: ['prop-midgard-pine', 'prop-midgard-pine2', 'prop-midgard-pine-b', 'prop-midgard-pine-c'], blockers: ['prop-midgard-icerock', 'prop-midgard-rock', 'prop-midgard-rock-c', 'prop-midgard-cairn'],
    decor: ['prop-midgard-grass', 'prop-midgard-grass', 'prop-midgard-snowbush'] }),
  taiga: B({ id: 'taiga', ground: [R.snow[0], R.snow[1], R.snow[2], R.snow[3]], patch: [R.frost[0], R.frost[1], R.frost[2], R.snow[1]], patchAmount: 0.14, forest: 0.24, water: 0.08,
    trees: ['prop-midgard-pine', 'prop-midgard-pine2', 'prop-midgard-pine-b', 'prop-midgard-pine-c', 'prop-midgard-deadtree', 'prop-midgard-deadtree-b'], blockers: ['prop-midgard-rock', 'prop-midgard-rock-c', 'prop-midgard-log'],
    decor: ['prop-midgard-grass', 'prop-midgard-snowbush', 'prop-midgard-log', 'prop-midgard-log-b'] }),
  moor: B({ id: 'moor', ground: [R.shade[0], R.grove[0], R.grove[1], R.grove[2]], patch: [R.shade[0], R.earth[0], R.earth[1], R.grove[1]], patchAmount: 0.22,
    liquid: [R.shade[0], R.tide[0], R.tide[1], R.tide[2]], water: 0.14, forest: 0.1,
    trees: ['prop-midgard-deadtree', 'prop-midgard-deadtree-b', 'prop-midgard-fir', 'prop-midgard-fir-b'], blockers: ['prop-midgard-runestone', 'prop-midgard-cairn', 'prop-midgard-rock-b'],
    decor: ['prop-midgard-grass', 'prop-midgard-bush', 'prop-midgard-grass'] }),
  crag: B({ id: 'crag', ground: [R.stone[0], R.earth[2], R.stone[2], R.stone[3]], patch: [R.earth[0], R.earth[1], R.earth[2], R.stone[2]], patchAmount: 0.18, cliff: [R.shade[0], R.stone[0], R.stone[1], R.stone[2]],
    ridge: 0.032, water: 0.05, forest: 0.06, rocks: 0.025,
    trees: ['prop-midgard-pine2', 'prop-midgard-deadtree', 'prop-midgard-deadtree-b'], blockers: ['prop-midgard-rock', 'prop-midgard-rock-b', 'prop-midgard-rock-c', 'prop-midgard-cairn', 'prop-midgard-icerock'],
    decor: ['prop-midgard-grass'] }),
  fjord: B({ id: 'fjord', ground: [R.snow[0], R.snow[1], R.snow[2], R.snow[3]], patch: [R.stone[1], R.stone[2], R.snow[1], R.snow[2]], water: 0.34, ridge: 0.018, forest: 0.08,
    trees: ['prop-midgard-pine', 'prop-midgard-pine2', 'prop-midgard-pine-b'], blockers: ['prop-midgard-rock', 'prop-midgard-rock-c', 'prop-midgard-icerock'],
    decor: ['prop-midgard-reeds', 'prop-midgard-grass', 'prop-midgard-reeds'] }),
  fields: B({ id: 'fields', ground: [R.earth[1], R.light[0], R.light[1], R.light[2]], patch: [R.earth[1], R.grove[1], R.grove[2], R.light[1]], patchAmount: 0.2, water: 0.08, forest: 0.07,
    trees: ['prop-midgard-fir', 'prop-midgard-fir-b', 'prop-midgard-deadtree', 'prop-midgard-deadtree-b'], blockers: ['prop-midgard-rock-b', 'prop-midgard-cairn', 'prop-midgard-log'],
    decor: ['prop-midgard-grass', 'prop-midgard-grass', 'prop-midgard-bush'], decorDensity: 0.08 }),
  ember: B({ id: 'ember', ground: [R.shade[0], R.shade[1], R.earth[0], R.earth[1]], patch: [R.stone[0], R.stone[1], R.earth[1], R.earth[2]],
    liquid: [R.flame[0], R.flame[1], R.flame[2], R.flame[3]], liquidKind: 'lava', cliff: [R.shade[0], R.stone[0], R.stone[1], R.earth[2]],
    water: 0.16, ridge: 0.026, forest: 0.05, rocks: 0.02,
    trees: ['prop-midgard-deadtree', 'prop-midgard-deadtree-b'], blockers: ['prop-midgard-lavarock', 'prop-midgard-rock-b'], decor: ['prop-midgard-emberbush', 'prop-midgard-emberbush'] }),
  glacier: B({ id: 'glacier', ground: [R.frost[1], R.frost[2], R.snow[2], R.ice[3]], patch: [R.ice[1], R.ice[2], R.frost[2], R.snow[2]],
    liquid: [R.ice[0], R.ice[1], R.ice[2], R.ice[3]], liquidKind: 'ice', cliff: [R.frost[0], R.frost[1], R.frost[2], R.snow[3]],
    water: 0.16, ridge: 0.026, forest: 0.04,
    trees: ['prop-midgard-pine2'], blockers: ['prop-midgard-icerock', 'prop-midgard-icerock', 'prop-midgard-cairn'], decor: ['prop-midgard-grass'], decorDensity: 0.02 }),
  meadow: B({ id: 'meadow', ground: [R.grove[0], R.grove[1], R.grove[2], R.grove[3]], patch: [R.earth[1], R.earth[2], R.earth[3], R.grove[2]], patchAmount: 0.16, water: 0.1, forest: 0.12,
    cliff: [R.earth[0], R.cream[0], R.cream[1], R.cream[2]],
    trees: ['prop-hellas-olive', 'prop-hellas-olive-b', 'prop-hellas-cypress', 'prop-hellas-cypress-b'], blockers: ['prop-hellas-rock', 'prop-hellas-rock-b', 'prop-hellas-rock-c', 'prop-hellas-column'],
    decor: ['prop-hellas-flowers', 'prop-hellas-grass', 'prop-hellas-bush', 'prop-hellas-grass'], decorDensity: 0.08 }),
  coast: B({ id: 'coast', ground: [R.cream[0], R.cream[1], R.light[2], R.cream[2]], patch: [R.grove[1], R.grove[2], R.cream[0], R.light[2]], patchAmount: 0.2, water: 0.38, forest: 0.06,
    trees: ['prop-hellas-cypress', 'prop-hellas-olive', 'prop-hellas-olive-b'], blockers: ['prop-hellas-rock', 'prop-hellas-rock-b', 'prop-hellas-rock-c', 'prop-hellas-amphora'],
    decor: ['prop-hellas-shell', 'prop-hellas-grass', 'prop-hellas-reeds'] }),
  ruins: B({ id: 'ruins', ground: [R.earth[1], R.earth[2], R.earth[3], R.cream[0]], patch: [R.earth[1], R.stone[1], R.stone[2], R.earth[3]], patchAmount: 0.25, water: 0.06, forest: 0.06, rocks: 0.03,
    cliff: [R.earth[0], R.stone[1], R.stone[2], R.stone[3]],
    trees: ['prop-hellas-cypress', 'prop-hellas-cypress-b'], blockers: ['prop-hellas-column', 'prop-hellas-columnfallen', 'prop-hellas-amphora', 'prop-hellas-rock', 'prop-hellas-rock-b'],
    decor: ['prop-hellas-flowers', 'prop-hellas-grass'] }),
  marsh: B({ id: 'marsh', ground: [R.grove[0], R.grove[0], R.grove[1], R.grove[2]], patch: [R.earth[0], R.earth[1], R.grove[1], R.grove[2]], liquid: [R.shade[0], R.tide[0], R.grove[0], R.tide[2]],
    water: 0.3, ridge: 0.02, forest: 0.1,
    trees: ['prop-hellas-cypress', 'prop-hellas-cypress-b'], blockers: ['prop-hellas-rock', 'prop-hellas-rock-c'], decor: ['prop-hellas-reeds', 'prop-hellas-reeds', 'prop-hellas-grass', 'prop-hellas-bush'], decorDensity: 0.1 }),
  underworld: B({ id: 'underworld', ground: [R.shade[0], R.shade[1], R.shade[2], R.storm[1]], patch: [R.stone[0], R.stone[1], R.shade[2], R.shade[3]],
    liquid: [R.storm[0], R.storm[1], R.violet[1], R.storm[3]], liquidKind: 'styx', cliff: [R.shade[0], R.stone[0], R.stone[1], R.shade[3]],
    water: 0.2, ridge: 0.024, forest: 0.05,
    trees: ['prop-midgard-deadtree', 'prop-midgard-deadtree-b'], blockers: ['prop-midgard-rock-b', 'prop-hellas-columnfallen'], decor: ['prop-hellas-flowers', 'prop-hellas-grass'], decorDensity: 0.04 }),
  ash: B({ id: 'ash', ground: [R.stone[0], R.stone[1], R.earth[1], R.stone[2]], patch: [R.earth[0], R.earth[1], R.stone[1], R.stone[2]],
    liquid: [R.flame[0], R.flame[1], R.flame[2], R.flame[3]], liquidKind: 'lava', cliff: [R.shade[0], R.stone[0], R.stone[1], R.stone[2]],
    water: 0.14, ridge: 0.028, forest: 0.03, rocks: 0.025,
    trees: ['prop-midgard-deadtree', 'prop-midgard-deadtree-b'], blockers: ['prop-midgard-rock-b', 'prop-midgard-lavarock'], decor: ['prop-midgard-emberbush'] }),
  highland: B({ id: 'highland', ground: [R.grove[0], R.grove[1], R.grove[2], R.grove[3]], patch: [R.earth[1], R.earth[2], R.earth[3], R.grove[2]], patchAmount: 0.14, ridge: 0.018, water: 0.06, forest: 0.08,
    cliff: [R.earth[0], R.cream[0], R.cream[1], R.cream[3]],
    trees: ['prop-hellas-cypress', 'prop-hellas-cypress-b', 'prop-hellas-olive', 'prop-hellas-olive-b'], blockers: ['prop-hellas-rock', 'prop-hellas-rock-b', 'prop-hellas-column'], decor: ['prop-hellas-flowers', 'prop-hellas-grass'], decorDensity: 0.08 }),
  // ---- Yomi ----
  bamboo: B({ id: 'bamboo', ground: [R.grove[0], R.grove[1], R.grove[2], R.grove[3]], patch: [R.earth[0], R.earth[1], R.grove[1], R.grove[2]], patchAmount: 0.18, forest: 0.26, water: 0.08,
    trees: ['prop-yomi-bamboo', 'prop-yomi-bamboo-b', 'prop-yomi-bamboo-c', 'prop-yomi-sakura'], blockers: ['prop-yomi-rock', 'prop-yomi-rock-b', 'prop-yomi-lantern'],
    decor: ['prop-yomi-grass', 'prop-yomi-shrub', 'prop-yomi-grass'], decorDensity: 0.08 }),
  shrine: B({ id: 'shrine', ground: [R.grove[0], R.grove[1], R.grove[2], R.grove[3]], patch: [R.earth[1], R.earth[2], R.cream[0], R.cream[1]], patchAmount: 0.3, forest: 0.08, water: 0.06,
    cliff: [R.earth[0], R.stone[1], R.stone[2], R.stone[3]],
    trees: ['prop-yomi-sakura', 'prop-yomi-sakura-b', 'prop-yomi-sakura-c', 'prop-yomi-bamboo'], blockers: ['prop-yomi-torii', 'prop-yomi-lantern', 'prop-yomi-lantern', 'prop-yomi-rock-c'],
    decor: ['prop-yomi-lily', 'prop-yomi-grass', 'prop-yomi-shrub'], decorDensity: 0.07 }),
  river: B({ id: 'river', ground: [R.grove[0], R.grove[1], R.grove[2], R.grove[3]], patch: [R.stone[1], R.stone[2], R.grove[1], R.grove[2]], patchAmount: 0.18, water: 0.34, forest: 0.1,
    trees: ['prop-yomi-bamboo', 'prop-yomi-bamboo-b', 'prop-yomi-sakura', 'prop-yomi-sakura-b'], blockers: ['prop-yomi-rock', 'prop-yomi-rock-b', 'prop-yomi-rock-c', 'prop-yomi-lantern'],
    decor: ['prop-yomi-grass', 'prop-hellas-reeds', 'prop-yomi-grass'] }),
  graveyard: B({ id: 'graveyard', ground: [R.shade[0], R.shade[1], R.grove[0], R.grove[1]], patch: [R.stone[0], R.stone[1], R.shade[2], R.stone[2]], patchAmount: 0.22,
    liquid: [R.shade[0], R.storm[0], R.tide[0], R.ice[1]], liquidKind: 'styx', cliff: [R.shade[0], R.stone[0], R.stone[1], R.stone[2]],
    water: 0.14, ridge: 0.02, forest: 0.06,
    trees: ['prop-midgard-deadtree', 'prop-midgard-deadtree-b', 'prop-yomi-sakura-b'], blockers: ['prop-yomi-grave', 'prop-yomi-grave', 'prop-yomi-lantern'],
    decor: ['prop-yomi-lily', 'prop-yomi-lily', 'prop-yomi-grass'], decorDensity: 0.06 }),
  peak: B({ id: 'peak', ground: [R.stone[0], R.stone[1], R.stone[2], R.stone[3]], patch: [R.grove[0], R.grove[1], R.earth[1], R.stone[2]], patchAmount: 0.2,
    cliff: [R.shade[0], R.stone[0], R.stone[1], R.stone[2]], ridge: 0.032, water: 0.05, forest: 0.1, rocks: 0.025,
    trees: ['prop-midgard-fir', 'prop-midgard-fir-b', 'prop-yomi-bamboo-c', 'prop-yomi-sakura-c'], blockers: ['prop-yomi-rock', 'prop-yomi-rock-b', 'prop-yomi-rock-c', 'prop-yomi-torii'],
    decor: ['prop-yomi-grass', 'prop-yomi-shrub'] }),
  village: B({ id: 'village', ground: [R.earth[1], R.earth[2], R.earth[3], R.cream[0]], patch: [R.grove[0], R.grove[1], R.grove[2], R.earth[2]], patchAmount: 0.24, water: 0.1, forest: 0.07,
    trees: ['prop-yomi-sakura', 'prop-yomi-sakura-c', 'prop-yomi-bamboo', 'prop-yomi-bamboo-c'], blockers: ['prop-yomi-lantern', 'prop-yomi-rock', 'prop-yomi-torii'],
    decor: ['prop-yomi-grass', 'prop-yomi-lily', 'prop-yomi-shrub'], decorDensity: 0.06 }),
};

/** Area id -> biome. Unlisted areas fall back to the region default. */
const AREA_BIOME: Record<string, string> = {
  'midgard-1': 'snow', 'midgard-2': 'taiga', 'midgard-3': 'moor', 'midgard-4': 'crag', 'midgard-5': 'crag',
  'midgard-6': 'fjord', 'midgard-7': 'fields', 'midgard-8': 'moor', 'midgard-9': 'ember', 'midgard-10': 'moor',
  'midgard-11': 'fjord', 'midgard-12': 'glacier', 'midgard-13': 'ember', 'midgard-14': 'taiga', 'midgard-15': 'glacier',
  'midgard-16': 'taiga', 'midgard-17': 'glacier', 'midgard-18': 'crag', 'midgard-19': 'fjord',
  'hellas-1': 'meadow', 'hellas-2': 'coast', 'hellas-3': 'ash', 'hellas-4': 'marsh', 'hellas-5': 'ruins',
  'hellas-6': 'coast', 'hellas-7': 'highland', 'hellas-8': 'underworld', 'hellas-9': 'meadow', 'hellas-10': 'ash',
  'hellas-11': 'ruins', 'hellas-12': 'coast', 'hellas-13': 'underworld', 'hellas-14': 'highland', 'hellas-15': 'ash',
  'yomi-1': 'bamboo', 'yomi-2': 'shrine', 'yomi-3': 'peak', 'yomi-4': 'river', 'yomi-5': 'graveyard',
  'yomi-6': 'village', 'yomi-7': 'river', 'yomi-8': 'shrine', 'yomi-9': 'peak', 'yomi-10': 'shrine',
  'yomi-11': 'graveyard', 'yomi-12': 'peak', 'yomi-13': 'shrine', 'yomi-14': 'peak', 'yomi-15': 'graveyard',
};

// ---------------- noise ----------------
function hash2(seed: number, x: number, y: number): number {
  let h = Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 2147483647);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
const smooth = (t: number) => t * t * (3 - 2 * t);
export function vnoise(seed: number, x: number, y: number): number {
  const x0 = Math.floor(x), y0 = Math.floor(y);
  const fx = smooth(x - x0), fy = smooth(y - y0);
  const a = hash2(seed, x0, y0), b = hash2(seed, x0 + 1, y0), c = hash2(seed, x0, y0 + 1), d = hash2(seed, x0 + 1, y0 + 1);
  return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
}
function fbm(seed: number, x: number, y: number): number {
  return (vnoise(seed, x, y) * 0.6 + vnoise(seed + 17, x * 2.1, y * 2.1) * 0.28 + vnoise(seed + 31, x * 4.3, y * 4.3) * 0.12);
}
export function hashString(s: string): number {
  let h = 2166136261;
  for (const ch of s) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return h >>> 0;
}
/** Deterministic per-tile random number. */
export function tileRand(seed: number, x: number, y: number, salt = 0) { return hash2(seed + salt * 7919, x, y); }

// ---------------- maps ----------------
export interface MapProp { x: number; y: number; id: string; block: boolean }

export interface WorldMap {
  key: string;
  seed: number;
  w: number;
  h: number;
  biome: Biome;
  tiles: Uint8Array;
  /** 1 where the tile is reachable on foot from the start. */
  reach: Uint8Array;
  /** Reachable tile indices (spawn candidates). */
  open: number[];
  start: { x: number; y: number };
  /** Thresholds, so the client can classify pixels the way tiles were classified. */
  th: { water: number; ridge: number };
  props: MapProp[];
}

/** Samples the terrain fields at a point in tile units. */
export function sample(m: Pick<WorldMap, 'seed' | 'th'>, x: number, y: number): 0 | 1 | 2 {
  const e = fbm(m.seed, x / 9, y / 9);
  if (e < m.th.water) return T_WATER;
  const r = fbm(m.seed + 101, x / 14, y / 14);
  // Passes: a second field breaks ridges up so they read as bands, not walls.
  if (Math.abs(r - 0.5) < m.th.ridge && vnoise(m.seed + 303, x / 4, y / 4) > 0.32) return T_CLIFF;
  return T_GROUND;
}

export function elevation(m: Pick<WorldMap, 'seed'>, x: number, y: number) { return fbm(m.seed, x / 9, y / 9); }

const cache = new Map<string, WorldMap>();

export function mapKeyFor(groundId: string): string {
  return GROUND[groundId]?.area ?? groundId;
}

export function mapFor(groundId: string): WorldMap {
  const key = mapKeyFor(groundId);
  let m = cache.get(key);
  if (!m) { m = generate(key); cache.set(key, m); }
  return m;
}

export function biomeFor(key: string): Biome {
  return BIOMES[AREA_BIOME[key] ?? (key.startsWith('hellas') ? 'meadow' : key.startsWith('yomi') ? 'bamboo' : 'snow')];
}

function quantile(values: number[], q: number) {
  const s = [...values].sort((a, b) => a - b);
  return s[Math.max(0, Math.min(s.length - 1, Math.floor(q * s.length)))];
}

function generate(key: string): WorldMap {
  const biome = biomeFor(key);
  const w = MAP_W, h = MAP_H;
  let waterFrac = biome.water, ridge = biome.ridge;
  for (let attempt = 0; ; attempt++) {
    const seed = hashString(key) + attempt * 977;
    const elev: number[] = [];
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) elev.push(fbm(seed, (x + 0.5) / 9, (y + 0.5) / 9));
    const m: WorldMap = {
      key, seed, w, h, biome, tiles: new Uint8Array(w * h), reach: new Uint8Array(w * h), open: [],
      start: { x: w / 2, y: h / 2 }, th: { water: waterFrac > 0 ? quantile(elev, waterFrac) : -1, ridge }, props: [],
    };
    const cx = w / 2, cy = h / 2;
    const forestVals: number[] = [];
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) forestVals.push(fbm(seed + 202, x / 6, y / 6));
    const forestT = quantile(forestVals, 1 - biome.forest);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = y * w + x;
      let t: number = sample(m, x + 0.5, y + 0.5);
      // Keep the middle clear so every map has room to start.
      if (Math.hypot(x + 0.5 - cx, (y + 0.5 - cy) * 1.3) < 3.5 && t !== T_GROUND) t = T_GROUND;
      if (t === T_GROUND) {
        const edge = x === 0 || y === 0 || x === w - 1 || y === h - 1;
        const f = fbm(seed + 202, x / 6, y / 6);
        if (edge && tileRand(seed, x, y, 1) < 0.75) t = T_TREE;
        else if (f > forestT && tileRand(seed, x, y, 2) < 0.8 && Math.hypot(x - cx, y - cy) > 4) t = T_TREE;
        else if (tileRand(seed, x, y, 3) < biome.rocks && Math.hypot(x - cx, y - cy) > 4) t = T_ROCK;
      }
      m.tiles[i] = t;
    }
    // Edges are always closed, so nothing walks off the map.
    for (let x = 0; x < w; x++) { if (m.tiles[x] === T_GROUND) m.tiles[x] = T_ROCK; if (m.tiles[(h - 1) * w + x] === T_GROUND) m.tiles[(h - 1) * w + x] = T_ROCK; }
    for (let y = 0; y < h; y++) { if (m.tiles[y * w] === T_GROUND) m.tiles[y * w] = T_ROCK; if (m.tiles[y * w + w - 1] === T_GROUND) m.tiles[y * w + w - 1] = T_ROCK; }

    m.start = { x: Math.floor(cx) + 0.5, y: Math.floor(cy) + 0.5 };
    const dist = bfs(m, Math.floor(cx), Math.floor(cy));
    let reach = 0;
    for (let i = 0; i < w * h; i++) if (dist[i] >= 0) { m.reach[i] = 1; m.open.push(i); reach++; }
    if (reach < w * h * 0.45 && attempt < 6) { waterFrac *= 0.75; ridge *= 0.75; continue; }

    // Props: trees and rocks on blocked tiles, decor on open ground.
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const t = m.tiles[y * w + x];
      const r = tileRand(seed, x, y, 4);
      if (t === T_TREE && biome.trees.length) m.props.push({ x: x + 0.2 + tileRand(seed, x, y, 8) * 0.6, y: y + 0.3 + tileRand(seed, x, y, 10) * 0.4, id: biome.trees[Math.floor(r * biome.trees.length)], block: true });
      else if (t === T_ROCK && biome.blockers.length) m.props.push({ x: x + 0.5, y: y + 0.5, id: biome.blockers[Math.floor(r * biome.blockers.length)], block: true });
      else if (t === T_GROUND && biome.decor.length && tileRand(seed, x, y, 5) < biome.decorDensity) {
        m.props.push({ x: x + 0.2 + tileRand(seed, x, y, 6) * 0.6, y: y + 0.3 + tileRand(seed, x, y, 7) * 0.5, id: biome.decor[Math.floor(r * biome.decor.length)], block: false });
      }
    }
    return m;
  }
}

export function walkable(m: WorldMap, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= m.w || y >= m.h) return false;
  return m.tiles[y * m.w + x] === T_GROUND;
}

const DIRS: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
const DX = DIRS.map(([dx]) => dx), DY = DIRS.map(([, dy]) => dy);

/** Steps from (sx, sy) to every tile; -1 where unreachable. Diagonals may not cut corners. */
export function bfs(m: WorldMap, sx: number, sy: number): Int16Array {
  const { w, h, tiles } = m;
  const dist = new Int16Array(w * h).fill(-1);
  if (!walkable(m, sx, sy)) return dist;
  const q = new Int32Array(w * h);
  let head = 0, tail = 0;
  q[tail++] = sy * w + sx;
  dist[sy * w + sx] = 0;
  // The hunt runs this on every re-plan of every player, so the loop works on tile indices (the same
  // checks as walkable(), in the same order: in bounds, open ground, no cut corner, not seen yet).
  while (head < tail) {
    const i = q[head++];
    const x = i % w, y = (i / w) | 0, d = dist[i] + 1;
    for (let k = 0; k < 8; k++) {
      const dx = DX[k], dy = DY[k];
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const j = ny * w + nx;
      if (tiles[j] !== T_GROUND) continue;
      if (dx && dy && (tiles[y * w + nx] !== T_GROUND || tiles[ny * w + x] !== T_GROUND)) continue;
      if (dist[j] >= 0) continue;
      dist[j] = d;
      q[tail++] = j;
    }
  }
  return dist;
}

/** Walk back from `goal` down a distance field to its source: tile centres from first step to goal. */
export function pathFrom(m: WorldMap, dist: Int16Array, gx: number, gy: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  let x = gx, y = gy;
  if (dist[y * m.w + x] < 0) return out;
  while (dist[y * m.w + x] > 0) {
    out.push({ x: x + 0.5, y: y + 0.5 });
    let best = -1, bx = x, by = y;
    for (const [dx, dy] of DIRS) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= m.w || ny >= m.h) continue;
      const d = dist[ny * m.w + nx];
      if (d < 0 || d >= dist[y * m.w + x]) continue;
      if (dx && dy && (!walkable(m, x + dx, y) || !walkable(m, x, y + dy))) continue;
      if (best < 0 || d < best) { best = d; bx = nx; by = ny; }
    }
    if (best < 0) break;
    x = bx; y = by;
  }
  return out.reverse();
}

/** Nearest walkable, reachable tile to a point (for repairing positions). */
export function nearestOpen(m: WorldMap, x: number, y: number): { x: number; y: number } {
  const tx = Math.floor(x), ty = Math.floor(y);
  if (walkable(m, tx, ty) && m.reach[ty * m.w + tx]) return { x, y };
  let best = m.open[0], bd = Infinity;
  for (const i of m.open) {
    const d = Math.hypot((i % m.w) + 0.5 - x, ((i / m.w) | 0) + 0.5 - y);
    if (d < bd) { bd = d; best = i; }
  }
  return { x: (best % m.w) + 0.5, y: ((best / m.w) | 0) + 0.5 };
}
